import { useState } from 'react';

import { useSubscription } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';

import { useCreateStockTransferMutation, useGetTransferableProductsFromCsvLazyQuery } from 'data/__generated__';
import { STOCK_TRANSFERS_UPDATED_SUBSCRIPTION } from 'data/subscriptions/stockTransfer';
import { colors } from 'constants/colors';
import { PAGES } from '../../constants/pages';

import { Footer } from 'components/Footer';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { ProductsToTransferTable } from './components/ProductsToTransferTable';
import { ColumnsSelector } from './components/ColumnsSelector';
import { PageTitle } from 'components/PageTitle';
import { DetailFormDatePicker } from 'components/DetailsView/DetailFormDatePicker';

import { dateFromString, formatDateAsAnniversary } from '../../helpers/dateTimes';
import { stockTransfersUpdatedHandler } from './utils/stockTransfersUpdatedHandler';
import { checkStockCsvImport } from '../../helpers/checkStockCsvImport';
import { TotemCsvInputButton, CsvDataRow } from 'components/TotemCsvInputButton';
import { StockTransferFormData, StockTransferFormSchema, defaultStockTransferValues } from './stockTransfer.schema';

export const StockTransferCreate = () => {
    const methods = useForm<StockTransferFormData>({
        resolver: zodResolver(StockTransferFormSchema),
        defaultValues: {
            ...defaultStockTransferValues,
            transferDate: formatDateAsAnniversary({ dateTime: new Date() }),
        },
    });

    const { handleSubmit, watch, setValue, control, reset } = methods;

    const formValues = watch();
    const [csvData, setCsvData] = useState<Array<CsvDataRow>>([]);
    const [isDestinationQuantity, setIsDestinationQuantity] = useState<boolean>(false);
    const [getCsvProducts, { loading: loadingCsv }] = useGetTransferableProductsFromCsvLazyQuery({
        fetchPolicy: 'network-only',
        onCompleted({ transferableProductsFromCsv }) {
            const { transferableProducts, idsNotFound, idsArchived } = transferableProductsFromCsv;
            if (transferableProducts?.length) {
                const currentProducts = formValues.products || [];
                const newProducts = transferableProducts.map((product) => ({
                    productId: product.productId,
                    quantity: product.quantity || 0,
                    packages: 0,
                    palette: 0,
                    fullname: product.fullname,
                    conditionning: product.conditionning,
                    packagesPerBatch: product.packagesPerBatch,
                    originStockAvailable: product.originStockAvailable ?? 0,
                    destinationStockAvailable: product.destinationStockAvailable ?? 0,
                }));
                setValue('products', [...currentProducts, ...newProducts], { shouldDirty: true });
            }
            if (idsNotFound?.length || idsArchived?.length) {
                let message = "Impossible d'ajouter les ids suivants =>";
                if (idsNotFound?.length) {
                    message += 'Ids non trouvés: ';
                    idsNotFound.forEach((id, index) => (message += `${index === 0 ? '' : ', '}${id}`));
                    message += <br />;
                }
                if (idsArchived?.length) {
                    message += 'Ids produits Archived: ';
                    idsArchived.forEach((id, index) => (message += `${index === 0 ? '' : ', '}${id}`));
                }

                toast.warn(message, { closeOnClick: false, autoClose: false });
            }
        },
    });
    const [createStockTransfer, { loading: creationLoading }] = useCreateStockTransferMutation();
    const navigate = useNavigate();

    useSubscription(STOCK_TRANSFERS_UPDATED_SUBSCRIPTION, {
        onData({
            data: {
                data: { stockTransferUpdated },
            },
        }) {
            stockTransfersUpdatedHandler(stockTransferUpdated);
        },
    });

    const handleCSVImport = async () => {
        const { success, error } = checkStockCsvImport({ csvData });
        if (!success) {
            setCsvData([]);
            toast.error(error, { autoClose: false });
            return;
        }
        const { originColumnId, destinationColumnId, transferDate } = formValues;
        if (!transferDate) {
            toast.error("La date de transfert n'a pas été renseignée");
            return;
        }
        if (!originColumnId) {
            toast.error("La colonne d'origine n'a pas été renseignée");
            return;
        }
        if (!destinationColumnId) {
            toast.error("La colonne de destination n'a pas été renseignée");
            return;
        }
        const [header, ...rawData] = csvData;
        const data = rawData.filter((row) => {
            return row.some((cell) => cell.trim() !== '');
        });
        const productIdIndex = header.findIndex((elem) => elem === 'productId');
        const quantityIndex = header.findIndex((elem) => elem === 'quantity');

        const csvDataFormatted = data.map((rowArray) => {
            const quantity = Number(rowArray[quantityIndex].replace(/\s+/g, '').replace(',', '.'));
            return { productId: rowArray[productIdIndex], quantity };
        });

        await getCsvProducts({
            variables: {
                originColumnId,
                destinationColumnId,
                csvData: csvDataFormatted,
                isDestinationQuantity,
            },
        });
    };

    const handleFormSubmit = handleSubmit(async (fields) => {
        const { transferDate, originColumnId, destinationColumnId, products } = fields;
        if (!transferDate) {
            toast.error("La date de transfert n'a pas été renseignée");
            return;
        }
        if (!originColumnId) {
            toast.error("La colonne d'origine n'a pas été renseignée");
            return;
        }
        if (!destinationColumnId) {
            toast.error("La colonne de destination n'a pas été renseignée");
            return;
        }
        const formattedProducts = products.map((product) => ({
            expiryDates: [], //for now we don't modify expiry dates when creating ST manually
            productId: product.productId,
            quantity: product.quantity,
        }));
        const result = await createStockTransfer({
            variables: {
                transferDate,
                originColumnId,
                destinationColumnId,
                products: formattedProducts,
            },
        });
        const { data } = result;
        if (data) {
            const {
                createStockTransfer: { success, stockTransfer },
            } = data;
            if (success && stockTransfer) {
                toast.success('Le transfert de stock a bien été créé !');
                const { _id: stockTransferId } = stockTransfer;
                navigate('/stock-transfer/' + stockTransferId);
                reset({
                    ...defaultStockTransferValues,
                    transferDate: formatDateAsAnniversary({ dateTime: new Date() }),
                });
            } else {
                const {
                    createStockTransfer: { error },
                } = data;
                if (error) {
                    toast.error(`L'erreur suivante est survenue : ${error}`);
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const handleToggle = () => {
        setIsDestinationQuantity(!isDestinationQuantity);
    };
    const hasColumnsSet = !!formValues.originColumnId && !!formValues.destinationColumnId;

    return (
        <Container>
            <FormProvider {...methods}>
                <Form>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.stockTransferCreate} />
                        </HeaderTitle>
                        {hasColumnsSet ? (
                            <>
                                {csvData.length ? (
                                    <ButtonContainer>
                                        <OptionLabel>
                                            Quantité à destination ?
                                            <input
                                                type="checkbox"
                                                checked={isDestinationQuantity}
                                                onClick={handleToggle}
                                            />
                                        </OptionLabel>
                                        <TotemPrimaryButton onClick={() => setCsvData([])}>
                                            <FaTrash data-test="trash-icon" size={12} color={colors.pureWhite} />
                                        </TotemPrimaryButton>
                                        <TotemPrimaryLoadingButton
                                            $minWidth="105px"
                                            loading={loadingCsv}
                                            useIcon={false}
                                            label="Import CSV"
                                            onClick={handleCSVImport}
                                        />
                                    </ButtonContainer>
                                ) : (
                                    <TotemCsvInputButton onCsvDataUpload={setCsvData} />
                                )}
                            </>
                        ) : null}
                        <Link to="/stock-transfers">
                            <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                        </Link>
                    </Header>
                    <Content>
                        <ContentScrollable>
                            <Fields>
                                <DetailFormDatePicker
                                    name="transferDate"
                                    label="Date du transfert"
                                    defaultValue={formValues.transferDate}
                                    control={
                                        control as unknown as Parameters<typeof DetailFormDatePicker>[0]['control']
                                    }
                                    transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                                    transformDateToValue={(date) =>
                                        date ? formatDateAsAnniversary({ dateTime: date }) : null
                                    }
                                />
                                <ColumnsSelector />
                                {hasColumnsSet ? (
                                    <ProductsToTransferTable
                                        originColumnId={formValues.originColumnId}
                                        destinationColumnId={formValues.destinationColumnId}
                                        transferDate={formValues.transferDate}
                                    />
                                ) : null}
                            </Fields>
                        </ContentScrollable>
                        <Footer>
                            <TotemPrimaryButton
                                onClick={handleFormSubmit}
                                disabled={creationLoading}
                                data-test="stock-transfer-create_submit-button"
                            >
                                Créer le transfert
                            </TotemPrimaryButton>
                        </Footer>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const ButtonContainer = styled.div`
    display: flex;
    width: max-content;
    align-items: center;
    gap: 5px;
`;

const OptionLabel = styled.div`
    font-size: 16px;
    color: ${({ theme }) => theme.textColor};
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    height: 100%;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    width: 100%;
`;

const ContentScrollable = styled.div`
    padding: 15px;
    width: 100%;
    flex: 1;
    overflow-y: auto;
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
